import React, { ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react'
import {
  ColumnDef,
  ExpandedState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, ChevronRight, Download } from 'lucide-react'
import { GlobalTable, HeaderLine, Tr, Td, FooterTr } from '../styles/table-style'
import { TableTooltipProvider, OverflowCell } from '../OverflowCell'
import LoadMoreButton from '../LoadMoreButton'
import { StandardColumn, RowAction } from './types'
import { ColumnsPreference } from '../../contexts/UserPreferencesContext'
import { resolveColumnOrder, resolveColumnLabel } from './columnUtils'
import ColumnSettings from './ColumnSettings'
import RowActionsMenu from './RowActionsMenu'
import { exportTableToExcel } from './exportToExcel'
import { useTableExportContext } from './TableExportContext'
import { useDelayedLoading } from '../../hooks/useDelayedLoading'
import {
  TableArea,
  TableToolbar,
  ToolbarSlot,
  ToolbarActions,
  ExportButton,
  TableScroll,
  SortableTh,
  ActionsTh,
  ActionsTd,
  GearTh,
  GearTd,
  ThContent,
  MutedSortIcon,
  ActiveSortIcon,
  EmptyMessage,
  ExpandCellContent,
  ExpandToggle,
  LoadingOverlay,
  LoadingSpinner,
  LoadingText,
} from './styles'

interface StandardDataTableProps<T> {
  columns: StandardColumn<T>[]
  data: T[]
  columnsState: ColumnsPreference
  onColumnsChange?: (next: ColumnsPreference) => void
  onRestoreColumns?: () => void
  totalCount?: number | null
  onLoadMore?: () => void
  hasMore?: boolean
  isLoading?: boolean
  countLabel?: string
  renderActions?: (row: T) => ReactNode
  rowActions?: (row: T) => RowAction[]
  actionsHeader?: string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string
  rowTitle?: (row: T) => string | undefined
  emptyMessage?: string
  toolbarLeft?: ReactNode
  exportFileName?: string
  exportSheetName?: string
  exportRows?: () => T[] | Promise<T[]>
  getSubRows?: (row: T) => T[] | undefined
  getRowId?: (row: T) => string
  expandColumnId?: string
  selectable?: boolean
  selectedRowIds?: Set<string | number>
  onSelectRow?: (id: string | number, selected: boolean) => void
  onSelectAllRows?: (selected: boolean) => void
  bulkHeaderRow?: ReactNode
}

const defaultCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toLocaleDateString('pt-BR')
  if (typeof value === 'object') return ''
  return String(value)
}

const resolveCellTooltipText = <T,>(col: StandardColumn<T>, row: T, rawValue: unknown): string => {
  if (col.exportValue) {
    const value = col.exportValue(row)
    return value === null || value === undefined ? '' : String(value)
  }
  return defaultCellValue(rawValue)
}

function StandardDataTable<T>({
  columns,
  data,
  columnsState,
  onColumnsChange,
  onRestoreColumns,
  totalCount = null,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  countLabel = 'itens',
  renderActions,
  rowActions,
  actionsHeader = 'Ações',
  onRowClick,
  rowClassName,
  rowTitle,
  emptyMessage = 'Nenhum registro encontrado',
  toolbarLeft,
  exportFileName,
  exportSheetName,
  exportRows,
  getSubRows,
  getRowId,
  expandColumnId,
  selectable,
  selectedRowIds,
  onSelectRow,
  onSelectAllRows,
  bulkHeaderRow,
}: StandardDataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [isExporting, setIsExporting] = useState(false)
  const showLoadingOverlay = useDelayedLoading(isLoading)

  const tableColumns = useMemo<ColumnDef<T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        accessorFn: col.accessor ?? ((row: T) => (row as Record<string, unknown>)?.[col.id]),
        header: resolveColumnLabel(col),
        enableSorting: col.sortable !== false,
        cell: (info) => {
          if (col.cell) {
            const rendered = col.cell(info.row.original)
            if (col.disableTooltip) return rendered
            const tooltipText = resolveCellTooltipText(col, info.row.original, info.getValue())
            if (!tooltipText) return rendered
            return <OverflowCell text={tooltipText}>{rendered}</OverflowCell>
          }
          const text = defaultCellValue(info.getValue())
          return <OverflowCell text={text} />
        },
      })),
    [columns],
  )

  const columnVisibility = useMemo(() => {
    const hidden = new Set(columnsState.hidden ?? [])
    return Object.fromEntries(columns.map((col) => [col.id, !hidden.has(col.id)]))
  }, [columns, columnsState])

  const columnOrder = useMemo(() => resolveColumnOrder(columns, columnsState), [columns, columnsState])

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { sorting, columnVisibility, columnOrder, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getSubRows,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  })

  const rows = table.getRowModel().rows
  const hasActionsColumn = !!rowActions || !!renderActions
  const exportContext = useTableExportContext()
  const exportRegistrationId = useId()
  const showColumnSettings = !!onColumnsChange && !!onRestoreColumns
  const showExport = !!exportFileName
  const showToolbarExport = showExport && !exportContext?.hasPageHeader
  const showToolbar = showToolbarExport || !!toolbarLeft
  const visibleColumnCount =
    table.getVisibleLeafColumns().length + (hasActionsColumn ? 1 : 0) + (showColumnSettings ? 1 : 0) + (selectable ? 1 : 0)

  const allSelected = data.length > 0 && !!selectedRowIds && data.every((item: any) => selectedRowIds.has(item.id))

  const handleExport = useCallback(async () => {
    if (!exportFileName || isExporting) return
    setIsExporting(true)
    try {
      const rowsToExport = exportRows
        ? await exportRows()
        : table.getRowModel().rows.filter((row) => row.depth === 0).map((row) => row.original)
      await exportTableToExcel({
        fileName: exportFileName,
        sheetName: exportSheetName,
        columns,
        columnsState,
        rows: rowsToExport,
      })
    } finally {
      setIsExporting(false)
    }
  }, [exportFileName, exportSheetName, exportRows, isExporting, table, columns, columnsState])

  const registerTableExport = exportContext?.registerTableExport
  const unregisterTableExport = exportContext?.unregisterTableExport

  useEffect(() => {
    if (!registerTableExport || !unregisterTableExport || !exportFileName) return
    registerTableExport({
      id: exportRegistrationId,
      onExport: handleExport,
      isExporting,
      disabled: data.length === 0,
    })
    return () => unregisterTableExport(exportRegistrationId)
  }, [registerTableExport, unregisterTableExport, exportFileName, exportRegistrationId, handleExport, isExporting, data.length])

  return (
    <TableTooltipProvider>
      <TableArea>
        {showToolbar && (
          <TableToolbar>
            <ToolbarSlot>{toolbarLeft}</ToolbarSlot>
            <ToolbarActions>
              {showToolbarExport && (
                <ExportButton onClick={handleExport} disabled={isExporting || data.length === 0}>
                  <Download size={14} />
                  {isExporting ? 'Exportando...' : 'Exportar excel'}
                </ExportButton>
              )}
            </ToolbarActions>
          </TableToolbar>
        )}
        <TableScroll>
          <GlobalTable>
            <thead>
              <HeaderLine>
                {selectable && (
                  <SortableTh style={{ width: '40px', minWidth: '40px', maxWidth: '40px', textAlign: 'center', padding: '6px' }}>
                    <ThContent style={{ justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#0090d3' }}
                        checked={allSelected}
                        onChange={(e) => onSelectAllRows?.(e.target.checked)}
                        title="Selecionar todos os estoques visíveis"
                      />
                    </ThContent>
                  </SortableTh>
                )}
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort()
                    const sortDirection = header.column.getIsSorted()
                    return (
                      <SortableTh
                        key={header.id}
                        $sortable={canSort}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <ThContent>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort &&
                            (sortDirection === 'asc' ? (
                              <ActiveSortIcon>
                                <ArrowUp size={14} />
                              </ActiveSortIcon>
                            ) : sortDirection === 'desc' ? (
                              <ActiveSortIcon>
                                <ArrowDown size={14} />
                              </ActiveSortIcon>
                            ) : (
                              <MutedSortIcon>
                                <ArrowUpDown size={14} />
                              </MutedSortIcon>
                            ))}
                        </ThContent>
                      </SortableTh>
                    )
                  }),
                )}
                {hasActionsColumn && <ActionsTh>{actionsHeader}</ActionsTh>}
                {showColumnSettings && (
                  <GearTh>
                    <ColumnSettings
                      columns={columns}
                      columnsState={columnsState}
                      onChange={onColumnsChange!}
                      onRestore={onRestoreColumns!}
                      iconOnly
                    />
                  </GearTh>
                )}
              </HeaderLine>
              {bulkHeaderRow}
            </thead>
            <tbody>
              {rows.length === 0 && !isLoading ? (
                <FooterTr>
                  <Td colSpan={visibleColumnCount}>
                    <EmptyMessage>{emptyMessage}</EmptyMessage>
                  </Td>
                </FooterTr>
              ) : (
                rows.map((row) => {
                  const rowId = getRowId ? getRowId(row.original) : ((row.original as any)?.id ?? row.id)
                  const isSelected = !!selectedRowIds && selectedRowIds.has(rowId)
                  const canExpand = !!getSubRows && row.getCanExpand()
                  const handleRowClick = onRowClick
                    ? () => onRowClick(row.original)
                    : canExpand
                      ? () => row.toggleExpanded()
                      : undefined
                  return (
                    <Tr
                      key={row.id}
                      className={rowClassName ? rowClassName(row.original) : undefined}
                      title={rowTitle ? rowTitle(row.original) : undefined}
                      onClick={handleRowClick}
                      style={{
                        ...(row.depth > 0 ? { backgroundColor: '#fafbfc' } : {}),
                        ...(isSelected ? { backgroundColor: '#e0f2fe', borderLeft: '4px solid #0090d3' } : {}),
                      }}
                    >
                      {selectable && (
                        <Td
                          style={{ width: '40px', minWidth: '40px', maxWidth: '40px', textAlign: 'center', padding: '6px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#0090d3' }}
                            checked={isSelected}
                            onChange={(e) => onSelectRow?.(rowId, e.target.checked)}
                          />
                        </Td>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <Td key={cell.id}>
                          {expandColumnId && cell.column.id === expandColumnId ? (
                            <ExpandCellContent $depth={row.depth}>
                              {canExpand && (
                                <ExpandToggle
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    row.toggleExpanded()
                                  }}
                                >
                                  {row.getIsExpanded() ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </ExpandToggle>
                              )}
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </ExpandCellContent>
                          ) : (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          )}
                        </Td>
                      ))}
                      {hasActionsColumn && (
                        <ActionsTd className="edit-div">
                          {rowActions ? (
                            <RowActionsMenu actions={rowActions(row.original)} />
                          ) : (
                            renderActions!(row.original)
                          )}
                        </ActionsTd>
                      )}
                      {showColumnSettings && <GearTd />}
                    </Tr>
                  )
                })
              )}
            </tbody>
            {onLoadMore && (
              <tfoot>
                <FooterTr>
                  <Td colSpan={visibleColumnCount}>
                    <LoadMoreButton
                      currentCount={data.length}
                      totalCount={totalCount}
                      onLoadMore={onLoadMore}
                      hasMore={hasMore}
                      isLoading={isLoading}
                      label={countLabel}
                    />
                  </Td>
                </FooterTr>
              </tfoot>
            )}
          </GlobalTable>
        </TableScroll>
        {showLoadingOverlay && (
          <LoadingOverlay>
            <LoadingSpinner />
            <LoadingText>Carregando...</LoadingText>
          </LoadingOverlay>
        )}
      </TableArea>
    </TableTooltipProvider>
  )
}

export default StandardDataTable
